import React from 'react';
import T from '../../styles/theme';
import FieldEditor from '../FieldEditor';
import { useFieldOptions } from '../../data/store';

function evalCalcField(field, entity, store) {
  try {
    let formula = field.formula || "";
    if (formula.includes("deal_count")) {
      const dealCount = (store?.deals || []).filter(d => d.ct === entity.id || d.co === entity.co).length;
      formula = formula.replace(/deal_count/g, dealCount);
    }
    if (formula.includes("total_deal_value")) {
      const total = (store?.deals || []).filter(d => d.ct === entity.id || d.co === entity.co).reduce((s, d) => s + (d.val || 0), 0);
      formula = formula.replace(/total_deal_value/g, total);
    }
    if (formula.includes("days_since_created")) {
      const created = entity.created ? new Date(entity.created) : new Date();
      const days = Math.floor((Date.now() - created.getTime()) / 86400000);
      formula = formula.replace(/days_since_created/g, days);
    }
    if (formula.includes("days_since_activity")) {
      const last = entity.lastActivity ? new Date(entity.lastActivity) : new Date();
      const days = Math.floor((Date.now() - last.getTime()) / 86400000);
      formula = formula.replace(/days_since_activity/g, days);
    }
    const allFields = Object.keys(entity);
    for (const key of allFields) {
      const val = entity[key];
      if (typeof val === "number") {
        formula = formula.replace(new RegExp("\\b" + key + "\\b", "g"), val);
      }
    }
    const safe = formula.replace(/[^0-9+\-*/().,%\s]/g, "");
    // eslint-disable-next-line no-eval
    const result = Function('"use strict"; return (' + safe + ')')();
    return isNaN(result) ? 0 : result;
  } catch (e) {
    return 0;
  }
}

export function PropRow({ label, value, editable, onChange, type, options }) {
  return editable ? (
    <FieldEditor label={label} value={value} type={type || "text"} options={options || []} onSave={onChange} />
  ) : (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid " + T.bdr, minHeight: 32 }}>
      <span style={{ fontSize: 11, color: T.txt, minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 11, color: value ? T.txt : T.tx3, textAlign: "right", flex: 1 }}>{value || "—"}</span>
    </div>
  );
}

export default function ContactForm({ values, onChange, teamMembers, getOwnerName, customFields, store }) {
  const c = values || {};
  const props = c.properties || {};

  // Tier B options — reactive to admin-saved overrides via store.customFields.__overrides
  const sourceOptions   = useFieldOptions("contacts", "source");
  const typeOptions     = useFieldOptions("contacts", "type");
  const industryOptions = useFieldOptions("contacts", "industry");

  const ownerOptions = (teamMembers || [])
    .map(m => m.name || (m.firstName + " " + m.lastName))
    .filter(Boolean);

  const ownerDisplay = typeof getOwnerName === "function" ? getOwnerName(c.owner) : (c.owner || "");

  const handleOwnerChange = (v) => {
    const member = (teamMembers || []).find(m => (m.name || (m.firstName + " " + m.lastName)) === v);
    onChange({ owner: member?._dbId || v });
  };

  return (
    <div>
      <PropRow label="First name" value={c.fn} editable onChange={v => onChange({ fn: v })} />
      <PropRow label="Last name" value={c.ln} editable onChange={v => onChange({ ln: v })} />
      <PropRow label="Job title" value={c.title} editable onChange={v => onChange({ title: v })} />
      <PropRow label="Company" value={c.co} editable onChange={v => onChange({ co: v })} />
      <PropRow label="Contact owner" value={ownerDisplay} editable onChange={handleOwnerChange} type="select" options={ownerOptions} />
      <PropRow label="Email" value={c.email} editable onChange={v => onChange({ email: v })} type="email" />
      <PropRow label="Phone" value={c.ph} editable onChange={v => onChange({ ph: v })} type="phone" />
      <PropRow label="Lead source" value={c.source} editable onChange={v => onChange({ source: v })} type="select" options={sourceOptions} />
      <PropRow label="Account type" value={c.type} editable onChange={v => onChange({ type: v })} type="select" options={typeOptions} />
      <PropRow label="Industry" value={c.industry} editable onChange={v => onChange({ industry: v })} type="select" options={industryOptions} />
      <PropRow label="City" value={c.city} editable onChange={v => onChange({ city: v })} />
      <PropRow label="State/Region" value={c.state} editable onChange={v => onChange({ state: v })} />
      <PropRow label="Annual revenue" value={props.annualRevenue} editable onChange={v => onChange({ properties: { ...props, annualRevenue: v } })} type="currency" />
      <PropRow label="Employees" value={props.employees} editable onChange={v => onChange({ properties: { ...props, employees: v } })} type="number" />
      <PropRow label="Fiscal year" value={props.fiscalYear} editable onChange={v => onChange({ properties: { ...props, fiscalYear: v } })} />
      <PropRow label="Created date" value={c.created} />
      <PropRow label="Last activity" value={c.lastActivity} />

      {(customFields || []).length > 0 && (
        <div style={{ borderTop: "1px solid " + T.bdr, marginTop: 8, paddingTop: 8 }}>
          <div style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>CUSTOM FIELDS</div>
          {(customFields || []).map(f => {
            if (f.type === "calculation") {
              const val = evalCalcField(f, c, store);
              const display = f.calcFormat === "currency" ? "$" + Number(val).toLocaleString() : f.calcFormat === "percent" ? Number(val).toFixed(1) + "%" : Number(val).toLocaleString();
              return <PropRow key={f.id} label={f.label} value={display} />;
            }
            return (
              <PropRow
                key={f.id}
                label={f.label}
                value={f.type === "checkbox" ? (c[f.id] ? "Yes" : "No") : (c[f.id] || "")}
                editable
                onChange={v => onChange({ [f.id]: f.type === "checkbox" ? (v === "Yes") : v })}
                type={f.type === "checkbox" ? "select" : (f.type || "text")}
                options={f.type === "checkbox" ? ["Yes", "No"] : (f.options || [])}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
